/**
 * Edit Assessment Page
 *
 * Edit a draft assessment's name, objective, and assessment timing.
 * Only draft assessments can be edited.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../lib/api';

import { RequestPanel } from '../../components/RequestPanel';
import type { ApiResult, Assessment } from '../../lib/types';

interface FormData {
  name: string;
  objective: string;
  assessmentType: 'pre' | 'post';
}

export function EditAssessment() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<ApiResult<Assessment> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadAssessment = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const res = await apiClient.getAssessment(id);
    setResult(res);
    if (res.response.data) {
      const a = res.response.data;
      setAssessment(a);
      // Extract name and objective from metadata
      const metadata = a.metadata || {};
      setFormData({
        name: (metadata['name'] as string) || '',
        objective: (metadata['objective'] as string) || '',
        assessmentType: a.assessmentType || 'pre',
      });
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    loadAssessment();
  }, [loadAssessment]);

  const validate = (): boolean => {
    if (!formData) return false;

    const trimmedName = formData.name.trim();
    const trimmedObjective = formData.objective.trim();

    if (!trimmedName) {
      setError('Assessment name is required.');
      return false;
    }

    if (trimmedName.length > 200) {
      setError('Assessment name cannot exceed 200 characters.');
      return false;
    }

    if (!trimmedObjective) {
      setError('Assessment objective is required.');
      return false;
    }

    if (trimmedObjective.length > 2000) {
      setError('Assessment objective cannot exceed 2000 characters.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData || !id || !assessment || !validate()) return;

    setSaving(true);
    setResult(null);

    const res = await apiClient.updateAssessment(id, {
      name: formData.name.trim(),
      objective: formData.objective.trim(),
      assessmentType: formData.assessmentType,
      version: assessment.version,
    });

    setResult(res);
    setSaving(false);

    if (res.response.data) {
      navigate(`/console/trainer/assessments/${id}`);
    } else if (res.response.error) {
      // Handle version conflict specifically
      if (res.response.status === 409) {
        setError(
          'Version conflict: The assessment was modified by someone else. Please refresh and try again.'
        );
      } else {
        setError(`Failed to save: ${res.response.error.message}`);
      }
    }
  };

  if (loading || !formData) {
    return (
      <div>
        <h1>Edit Assessment</h1>
        <p className="text-muted">Loading...</p>
      </div>
    );
  }

  if (assessment && assessment.status !== 'draft') {
    return (
      <div>
        <div className="flex items-center gap-4 mb-4">
          <Link to={`/console/trainer/assessments/${id}`} style={{ color: 'var(--text-tertiary)' }}>
            &larr; Back to Assessment
          </Link>
        </div>
        <h1>Edit Assessment</h1>
        <div className="error-box">
          Only draft assessments can be edited. This assessment is {assessment.status}.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-4">
        <Link to={`/console/trainer/assessments/${id}`} style={{ color: 'var(--text-tertiary)' }}>
          &larr; Back to Assessment
        </Link>
      </div>

      <h1>Edit Assessment</h1>

      {error && (
        <div className="error-box" style={{ marginBottom: 16 }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="card" style={{ marginBottom: 16 }}>
          <h2>Assessment Details</h2>

          <div className="form-group">
            <label className="label">Assessment Name *</label>
            <input
              type="text"
              className="input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Customer Service Assessment"
              maxLength={200}
              style={{ maxWidth: 500 }}
            />
            <p className="text-muted text-small" style={{ marginTop: 4 }}>
              A descriptive name for this assessment (max 200 characters).
            </p>
          </div>

          <div className="form-group">
            <label className="label">Objective *</label>
            <textarea
              className="input"
              value={formData.objective}
              onChange={(e) => setFormData({ ...formData, objective: e.target.value })}
              placeholder="Describe what this assessment evaluates and what competencies are being measured."
              rows={4}
              maxLength={2000}
              style={{ maxWidth: 600, fontFamily: 'inherit' }}
            />
            <p className="text-muted text-small" style={{ marginTop: 4 }}>
              The learning objective or competency being assessed (max 2000 characters).
            </p>
          </div>

          <div className="form-group">
            <label className="label">Assessment Timing *</label>
            <select
              className="input"
              value={formData.assessmentType}
              onChange={(e) =>
                setFormData({ ...formData, assessmentType: e.target.value as 'pre' | 'post' })
              }
              style={{ maxWidth: 400 }}
            >
              <option value="pre">Pre-training</option>
              <option value="post">Post-training</option>
            </select>
            <p className="text-muted text-small" style={{ marginTop: 4 }}>
              When this assessment is taken relative to training.
            </p>
          </div>
        </div>

        <div className="card">
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || !formData.name.trim() || !formData.objective.trim()}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <Link to={`/console/trainer/assessments/${id}`} className="btn btn-secondary">
              Cancel
            </Link>
          </div>
        </div>
      </form>

      <RequestPanel result={result} loading={loading || saving} />
    </div>
  );
}
